/**
 * Filter section for the DeliveryTab.
 *
 * Grouped into labelled sections (Quick Ranges · Company & Supplier ·
 * Order · Status · Dates) so users can scan faster. Compact input sizing.
 */

import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Search, Filter, RefreshCw, Download } from 'lucide-react';
import { useView } from '../../../contexts/ViewContext';
import { coreAPI } from '../../../lib/api';
import { DeliveryFilterSupplierLayout } from './delivery/DeliveryFilterSupplierLayout';
import { DeliveryFilterCustomerLayout } from './delivery/DeliveryFilterCustomerLayout';

export function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const QUICK_RANGES = [
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'lastQuarter', label: 'Last Quarter' },
  { key: 'last12Months', label: 'Last 12 Months (inc. current month)' },
] as const;

export function getQuickRangeDates(range: string): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'lastMonth':
      return { dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)), dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const yr = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const q = lq < 0 ? 3 : lq;
      return { dateFrom: formatLocalDate(new Date(yr, q * 3, 1)), dateTo: formatLocalDate(new Date(yr, (q + 1) * 3, 0)) };
    }
    case 'last12Months':
      return { dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth() + 1, 1)), dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

interface FilterOption {
  value: string;
  label: string;
  group?: number | null;
}

interface DeliveryFilterSectionProps {
  filters: {
    grnNumber: string;
    poNumber: string;
    supplier: string;
    partNumber: string;
    lineNumber: string;
    inspection_status: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    warehouse: string;
    supplierGroup: string;
    commodityGroup: string;
    commodityType: string;
    appealStatus: string;
    appealFilter: string;
    poLineStatus: string;
    grnStatus: string;
    oddFrom: string;
    oddTo: string;
    strategic: string;
    supplierCode: string;
  };
  textInputs: {
    grnNumber: string;
    poNumber: string;
    supplier: string;
    partNumber: string;
    lineNumber: string;
  };
  // CR-6364: pending date values — applied to `filters` only on Search.
  pendingDates: {
    dateFrom: string;
    dateTo: string;
    oddFrom: string;
    oddTo: string;
  };
  siteOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  supplierOptions: FilterOption[];
  /** CR-6442 rework: options for the filters the design added. */
  divisionOptions?: FilterOption[];
  supplierCountryOptions?: string[];
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  onHideFilters?: () => void;
  /**
   * CR-6450: render the supplier-view layout from the supplied design —
   * four sections (Date Range / Company / Supplier / Order Details), a filter
   * header carrying the active-filter count plus Refresh / Export / Hide, and
   * a Clear all + Apply filters footer. The customer layout is unchanged.
   */
  supplierLayout?: boolean;
  /** Collapsed state. Supplier layout keeps its header visible when collapsed
   *  (per the design note: only the sections and footer hide). */
  expanded?: boolean;
  onShowFilters?: () => void;
  onRefresh?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  /** Number of filters currently applied, shown as a badge in the header. */
  activeFilterCount?: number;
}

export function DeliveryFilterSection({
  filters,
  textInputs,
  pendingDates,
  siteOptions,
  supplierGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  supplierOptions,
  divisionOptions = [],
  supplierCountryOptions = [],
  onFilterChange,
  onClearFilters,
  onSearch,
  onHideFilters,
  supplierLayout = false,
  expanded = true,
  onShowFilters,
  onRefresh,
  onExport,
  exporting = false,
  activeFilterCount = 0,
}: DeliveryFilterSectionProps) {
  // CR-4577: Filter site options by view context
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();

  // Warehouse dropdown bound to the admin-managed ``core.Warehouse`` list —
  // same source as the Schedule Lines filter panel.
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    // ``accessible`` action — open to all authenticated users and scoped to
    // their sites (the plain list endpoint is Helix-admin only and 403s for
    // regular customer users, leaving the dropdown empty).
    coreAPI.getAccessibleWarehouses()
      .then((res) => {
        if (!cancelled) {
          setWarehouseOptions((res.data || []).map((w) => ({ id: w.id, name: w.name })));
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);
  const viewScopedSites = useMemo(() => {
    if (viewLevel === 'SITE' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'COMPANY' && selectedCompanyId) return siteOptions.filter(s => s.value === String(selectedCompanyId));
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = siteOptions.filter(s => s.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : siteOptions;
    }
    return siteOptions;
  }, [siteOptions, viewLevel, selectedCompanyId, selectedGroupId]);

  // CR-6442 rework: one shell for both views. The supplier layout (CR-6450)
  // already matched the agreed structure — header that survives collapse,
  // sectioned body, Clear all / Apply filters footer — while the customer view
  // kept an older shell with no header actions and a Search / Clear All / Hide
  // row at the bottom left. QA failed the customer view for exactly that, and
  // for carrying a second Hide control in the view bar above. Only the section
  // body differs between the two now.
  const FilterLayout = supplierLayout
    ? DeliveryFilterSupplierLayout
    : DeliveryFilterCustomerLayout;

  return (
    <Card>
        {/* Filter header — stays visible when the panel is collapsed. The
            design note is explicit that hiding filters removes only the filter
            sections and the filter footer. Active filter count shown in the
            Filters button (matching Pricebooks tab pattern). */}
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base text-xl">Filter Deliveries</CardTitle>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <Button size="sm" variant="outline" onClick={onRefresh}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Refresh
                </Button>
              )}
              {onExport && (
                <Button size="sm" variant="outline" onClick={onExport} disabled={exporting}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Export
                </Button>
              )}
              <button
                onClick={expanded ? onHideFilters : onShowFilters}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                  expanded
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    expanded
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </CardHeader>
        {expanded && (
          <CardContent className="space-y-4 p-0">
            <FilterLayout
              filters={filters as unknown as Record<string, string>}
              textInputs={textInputs as unknown as Record<string, string>}
              pendingDates={pendingDates}
              quickRanges={QUICK_RANGES}
              getQuickRangeDates={getQuickRangeDates}
              viewScopedSites={viewScopedSites}
              warehouseOptions={warehouseOptions}
              supplierGroupOptions={supplierGroupOptions}
              supplierOptions={supplierOptions}
              commodityGroupOptions={commodityGroupOptions}
              commodityTypeOptions={commodityTypeOptions}
              divisionOptions={divisionOptions}
              supplierCountryOptions={supplierCountryOptions}
              onFilterChange={onFilterChange}
            />
            {/* Filter footer — Clear all left, Apply filters right. */}
            <div className="flex items-center justify-between px-6 pb-4">
              <Button size="sm" variant="outline" onClick={onClearFilters}>
                <X className="h-3.5 w-3.5 mr-1.5" />
                Clear all
              </Button>
              <Button size="sm" onClick={onSearch}>
                <Search className="h-3.5 w-3.5 mr-1.5" />
                Apply filters
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
  );
}
